import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, RefreshCw, PackagePlus, ShoppingCart, Boxes, History,
  Users, Link2, Copy, Trash2, AlertTriangle, Send, Settings2, FileSpreadsheet, TrendingUp, Calculator, Plus, ClipboardList, Tags, ChevronDown, ChevronRight, Pencil, Save, X, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import LabelsPage from './LabelsPage';

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(Math.round((n || 0) * 1000) / 1000);
const money = (n) => new Intl.NumberFormat('ru-RU').format(Math.round(n || 0)) + ' ₸';

function AdminProduction({ user, onLogout }) {
  const { lang } = useParams();
  const currentLang = lang || 'ru';
  const navigate = useNavigate();

  const [ingredients, setIngredients] = useState([]);
  const [reorder, setReorder] = useState([]);
  const [movements, setMovements] = useState([]);
  const [operators, setOperators] = useState([]);
  const [tab, setTab] = useState('stock');
  const [receiptFor, setReceiptFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [linkDialog, setLinkDialog] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const [margin, setMargin] = useState([]);
  const [marginLoading, setMarginLoading] = useState(false);
  const [reqData, setReqData] = useState(null);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqNotifying, setReqNotifying] = useState(false);
  const isoDay = (offset) => { const d = new Date(); d.setDate(d.getDate() + offset); return d.toISOString().slice(0, 10); };
  const [reqFrom, setReqFrom] = useState(() => isoDay(1));
  const [reqTo, setReqTo] = useState(() => isoDay(2));
  const [writeoff, setWriteoff] = useState(null);
  const [woMonth, setWoMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [addOpen, setAddOpen] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [recipeLink, setRecipeLink] = useState(null);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  const [autoOpen, setAutoOpen] = useState(false);
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const [editRecipe, setEditRecipe] = useState(null); // recipe object to edit, or 'new'
  const [orphans, setOrphans] = useState(null); // { totals, orphans[] } for products without a techkart
  const [showOrphans, setShowOrphans] = useState(false);
  const [orphanFilter, setOrphanFilter] = useState('online'); // 'online' | 'all'
  const [orphanQuery, setOrphanQuery] = useState('');

  const deleteRecipe = async (f) => {
    if (!window.confirm(`Удалить техкарту «${f.product_name}» полностью?`)) return;
    try {
      await axios.delete(`${API}/production/recipes/${f.recipe_id}`, { headers: getAuthHeaders() });
      toast.success('Техкарта удалена');
      loadRecipes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка удаления'); }
  };

  const load = async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        axios.get(`${API}/inventory/ingredients`, { headers: getAuthHeaders() }),
        axios.get(`${API}/inventory/reorder`, { headers: getAuthHeaders() }),
        axios.get(`${API}/inventory/movements?limit=80`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/production-operators`, { headers: getAuthHeaders() }),
      ]);
      setIngredients(a.data); setReorder(b.data); setMovements(c.data); setOperators(d.data);
    } catch (e) { toast.error('Ошибка загрузки'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const loadMargin = async () => {
    setMarginLoading(true);
    try { const { data } = await axios.get(`${API}/inventory/margin`, { headers: getAuthHeaders() }); setMargin(data); }
    catch (e) { toast.error('Ошибка загрузки маржи'); }
    finally { setMarginLoading(false); }
  };
  const loadWriteoff = async (month) => {
    try { const { data } = await axios.get(`${API}/inventory/writeoff-report?month=${month}`, { headers: getAuthHeaders() }); setWriteoff(data); }
    catch (e) { toast.error('Ошибка загрузки отчёта'); }
  };
  useEffect(() => {
    if (tab === 'margin' && margin.length === 0) loadMargin();
    if (tab === 'writeoff' && !writeoff) loadWriteoff(woMonth);
    if (tab === 'requirements' && !reqData) loadRequirements(reqFrom, reqTo);
    if (tab === 'recipes' && recipes.length === 0) loadRecipes();
    /* eslint-disable-next-line */
  }, [tab]);

  const loadRecipes = async () => {
    try {
      const [r, p, o] = await Promise.all([
        axios.get(`${API}/production/food-cost`, { headers: getAuthHeaders() }),
        axios.get(`${API}/products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/production/products-without-recipe`, { headers: getAuthHeaders() }),
      ]);
      setRecipes(r.data); setProducts(p.data); setOrphans(o.data);
    } catch (e) { toast.error('Ошибка загрузки техкарт'); }
  };
  const linkRecipe = async (recipeId, productId) => {
    try {
      await axios.post(`${API}/production/recipes/${recipeId}/link-product`, { product_id: productId || null }, { headers: getAuthHeaders() });
      toast.success(productId ? 'Товар привязан' : 'Привязка снята');
      setRecipeLink(null); loadRecipes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
  };
  const delIngredient = async (i) => {
    if (!window.confirm(`Удалить ингредиент «${i.name}»?`)) return;
    try { await axios.delete(`${API}/inventory/ingredients/${i.id}`, { headers: getAuthHeaders() }); toast.success('Удалено'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
  };

  const loadRequirements = async (from, to) => {
    setReqLoading(true);
    try { const { data } = await axios.get(`${API}/inventory/requirements?date_from=${from}&date_to=${to}`, { headers: getAuthHeaders() }); setReqData(data); }
    catch (e) { toast.error('Ошибка расчёта потребности'); }
    finally { setReqLoading(false); }
  };
  const presetReq = (a, b) => { const f = isoDay(a), t = isoDay(b); setReqFrom(f); setReqTo(t); loadRequirements(f, t); };
  const notifyRequirements = async () => {
    setReqNotifying(true);
    try {
      const { data } = await axios.post(`${API}/inventory/requirements/notify?date_from=${reqFrom}&date_to=${reqTo}`, {}, { headers: getAuthHeaders() });
      toast.success(`Отправлено в Telegram: ${data.items} позиций`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка отправки'); }
    finally { setReqNotifying(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Скопировано'); };

  const notifyReorder = async () => {
    setNotifying(true);
    try {
      const { data } = await axios.post(`${API}/inventory/reorder/notify`, {}, { headers: getAuthHeaders() });
      toast.success(`Отправлено в Telegram: ${data.items} позиций`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка отправки');
    } finally { setNotifying(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-production">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/${currentLang}/admin`)} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2"><Boxes className="w-5 h-5 text-amber-600" /> Склад и производство</h1>
              <p className="text-sm text-gray-500">Поступления (WAC), остатки, закупка, операторы мини-аппа</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/${currentLang}/admin/inventory-import`)} data-testid="import-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Импорт из Excel</Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="stock" data-testid="tab-stock"><Boxes className="w-4 h-4 mr-1" />Остатки</TabsTrigger>
            <TabsTrigger value="reorder" data-testid="tab-reorder"><ShoppingCart className="w-4 h-4 mr-1" />К закупке {reorder.length > 0 && <Badge className="ml-1 bg-red-100 text-red-700">{reorder.length}</Badge>}</TabsTrigger>
            <TabsTrigger value="movements" data-testid="tab-movements"><History className="w-4 h-4 mr-1" />Движения</TabsTrigger>
            <TabsTrigger value="operators" data-testid="tab-operators"><Users className="w-4 h-4 mr-1" />Операторы</TabsTrigger>
            <TabsTrigger value="writeoff" data-testid="tab-writeoff"><Trash2 className="w-4 h-4 mr-1" />Списания</TabsTrigger>
            <TabsTrigger value="margin" data-testid="tab-margin"><TrendingUp className="w-4 h-4 mr-1" />Маржа</TabsTrigger>
            <TabsTrigger value="requirements" data-testid="tab-requirements"><Calculator className="w-4 h-4 mr-1" />Потребность</TabsTrigger>
            <TabsTrigger value="recipes" data-testid="tab-recipes"><ClipboardList className="w-4 h-4 mr-1" />Техкарты</TabsTrigger>
            <TabsTrigger value="labels" data-testid="tab-labels"><Tags className="w-4 h-4 mr-1" />Наклейки</TabsTrigger>
          </TabsList>

          {/* STOCK */}
          <TabsContent value="stock">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={stockSearch}
                  onChange={(e) => setStockSearch(e.target.value)}
                  placeholder="Поиск ингредиента…"
                  className="pl-8 pr-8"
                  data-testid="stock-search-input"
                />
                {stockSearch && (
                  <button
                    type="button"
                    onClick={() => setStockSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="stock-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {stockSearch && (
                  <span className="text-xs text-gray-500" data-testid="stock-search-count">
                    {ingredients.filter((i) => (i.name || '').toLowerCase().includes(stockSearch.toLowerCase())).length} из {ingredients.length}
                  </span>
                )}
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAddOpen(true)} data-testid="add-ingredient-btn"><Plus className="w-4 h-4 mr-1" />Добавить ингредиент</Button>
              </div>
            </div>
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-600">
                  <tr><th className="text-left p-3">Ингредиент</th><th className="text-right p-3">Остаток</th><th className="text-right p-3">Ср. цена</th><th className="text-right p-3">Min/Max</th><th className="p-3"></th></tr>
                </thead>
                <tbody>
                  {ingredients
                    .filter((i) => !stockSearch || (i.name || '').toLowerCase().includes(stockSearch.toLowerCase()))
                    .map((i) => (
                    <tr key={i.id} className="border-t" data-testid={`ing-row-${i.id}`}>
                      <td className="p-3">{i.name} {i.low_stock && <AlertTriangle className="w-3.5 h-3.5 text-red-500 inline ml-1" />}</td>
                      <td className="p-3 text-right">{fmt(i.current_quantity)} {i.unit}</td>
                      <td className="p-3 text-right">{money(i.cost_per_unit)}</td>
                      <td className="p-3 text-right text-gray-500">{fmt(i.min_threshold)} / {fmt(i.max_threshold)}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="mr-1" onClick={() => setReceiptFor(i)} data-testid={`receipt-${i.id}`}><PackagePlus className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="mr-1" onClick={() => setEditFor(i)} data-testid={`edit-${i.id}`}><Settings2 className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => delIngredient(i)} data-testid={`del-ing-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                  {ingredients.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-400">Нет данных</td></tr>}
                  {ingredients.length > 0 && stockSearch && ingredients.filter((i) => (i.name || '').toLowerCase().includes(stockSearch.toLowerCase())).length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-gray-400" data-testid="stock-search-empty">Ничего не найдено по «{stockSearch}»</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* REORDER */}
          <TabsContent value="reorder">
            <div className="flex justify-end mb-3">
              <Button size="sm" variant="outline" disabled={reorder.length === 0 || notifying} onClick={notifyReorder} data-testid="notify-reorder-btn">
                <Send className="w-4 h-4 mr-1" />{notifying ? 'Отправка…' : 'Отправить в Telegram'}
              </Button>
            </div>
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-600"><tr><th className="text-left p-3">Ингредиент</th><th className="text-right p-3">Остаток</th><th className="text-right p-3">Min</th><th className="text-right p-3">Дозаказать</th></tr></thead>
                <tbody>
                  {reorder.map((i) => (
                    <tr key={i.id} className="border-t"><td className="p-3">{i.name}</td><td className="p-3 text-right text-red-600">{fmt(i.current_quantity)} {i.unit}</td><td className="p-3 text-right">{fmt(i.min_threshold)}</td><td className="p-3 text-right font-semibold">{i.suggested_order != null ? `+${fmt(i.suggested_order)} ${i.unit}` : '—'}</td></tr>
                  ))}
                  {reorder.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-gray-400">Все позиции выше минимума 👍</td></tr>}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* MOVEMENTS */}
          <TabsContent value="movements">
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-600"><tr><th className="text-left p-3">Когда</th><th className="text-left p-3">Тип</th><th className="text-left p-3">Позиция</th><th className="text-right p-3">Кол-во</th><th className="text-right p-3">Сумма</th><th className="text-left p-3">Кто</th></tr></thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id} className="border-t">
                      <td className="p-3 text-gray-500">{m.created_at ? new Date(m.created_at).toLocaleString('ru-RU') : '—'}</td>
                      <td className="p-3"><Badge variant="outline" className={m.movement_type === 'purchase' ? 'text-emerald-700' : m.movement_type === 'write_off' ? 'text-red-700' : 'text-gray-600'}>{m.movement_type}</Badge></td>
                      <td className="p-3">{m.ingredient_name || m.product_name}</td>
                      <td className="p-3 text-right">{fmt(m.quantity)} {m.unit}</td>
                      <td className="p-3 text-right">{money(m.cost)}</td>
                      <td className="p-3 text-gray-500">{m.created_by_name || '—'}</td>
                    </tr>
                  ))}
                  {movements.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-400">Нет движений</td></tr>}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* WRITE-OFF REPORT */}
          <TabsContent value="writeoff">
            <div className="flex items-center gap-2 mb-3">
              <Input type="month" value={woMonth} onChange={(e) => { setWoMonth(e.target.value); setWriteoff(null); }} className="w-44" data-testid="wo-month" />
              <Button size="sm" variant="outline" onClick={() => loadWriteoff(woMonth)} data-testid="wo-load-btn"><RefreshCw className="w-4 h-4 mr-1" />Показать</Button>
              {writeoff && <span className="text-sm text-gray-600 ml-auto">Итого за месяц: <b className="text-red-600" data-testid="wo-total">{money(writeoff.total_cost)}</b> · {writeoff.operations} опер.</span>}
            </div>
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-600"><tr><th className="text-left p-3">Ингредиент</th><th className="text-right p-3">Списано</th><th className="text-right p-3">Стоимость (WAC)</th><th className="text-right p-3">Операций</th></tr></thead>
                <tbody>
                  {(writeoff?.items || []).map((i, idx) => (
                    <tr key={idx} className="border-t"><td className="p-3">{i.ingredient_name}</td><td className="p-3 text-right">{fmt(i.quantity)} {i.unit}</td><td className="p-3 text-right font-semibold text-red-600">{money(i.cost)}</td><td className="p-3 text-right text-gray-500">{i.count}</td></tr>
                  ))}
                  {writeoff && writeoff.items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-gray-400">Списаний за этот месяц нет</td></tr>}
                  {!writeoff && <tr><td colSpan={4} className="p-6 text-center text-gray-400">Выберите месяц и нажмите «Показать»</td></tr>}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* MARGIN */}
          <TabsContent value="margin">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500">Себестоимость (WAC) против цены продажи. Отсортировано по марже (низкая — сверху).</p>
              <Button size="sm" variant="outline" onClick={loadMargin} data-testid="margin-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" />Обновить</Button>
            </div>
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-600"><tr><th className="text-left p-3">Продукт</th><th className="text-right p-3">Себестоимость</th><th className="text-right p-3">Цена</th><th className="text-right p-3">Маржа</th><th className="text-right p-3">Маржа %</th></tr></thead>
                <tbody>
                  {margin.map((m) => (
                    <tr key={m.recipe_id} className="border-t" data-testid={`margin-row-${m.recipe_id}`}>
                      <td className="p-3">{m.product_name} {m.missing_costs > 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 inline ml-1" title="Нет цены на часть ингредиентов" />}</td>
                      <td className="p-3 text-right">{money(m.cost_per_unit)}</td>
                      <td className="p-3 text-right">{m.has_price ? money(m.sell_price) : <span className="text-gray-400">нет цены</span>}</td>
                      <td className="p-3 text-right font-semibold">{m.margin != null ? money(m.margin) : '—'}</td>
                      <td className="p-3 text-right">{m.margin_pct != null ? <Badge className={m.margin_pct < 30 ? 'bg-red-100 text-red-700' : m.margin_pct < 50 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>{m.margin_pct}%</Badge> : '—'}</td>
                    </tr>
                  ))}
                  {margin.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-400">{marginLoading ? 'Загрузка…' : 'Нет данных'}</td></tr>}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* REQUIREMENTS (planning) */}
          <TabsContent value="requirements">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Button size="sm" variant="outline" onClick={() => presetReq(1, 2)} data-testid="req-preset-2d">Завтра + послезавтра</Button>
              <Button size="sm" variant="outline" onClick={() => presetReq(1, 7)} data-testid="req-preset-7d">7 дней</Button>
              <span className="mx-1 text-gray-300">|</span>
              <Input type="date" value={reqFrom} onChange={(e) => setReqFrom(e.target.value)} className="w-40" data-testid="req-from" />
              <span className="text-gray-400">—</span>
              <Input type="date" value={reqTo} onChange={(e) => setReqTo(e.target.value)} className="w-40" data-testid="req-to" />
              <Button size="sm" variant="outline" onClick={() => loadRequirements(reqFrom, reqTo)} data-testid="req-load-btn"><RefreshCw className="w-4 h-4 mr-1" />Рассчитать</Button>
              <Button size="sm" className="ml-auto bg-amber-600 hover:bg-amber-700" disabled={!reqData || (reqData?.buy_list?.length || 0) === 0 || reqNotifying} onClick={notifyRequirements} data-testid="req-notify-btn">
                <Send className="w-4 h-4 mr-1" />{reqNotifying ? 'Отправка…' : 'Список к закупке в Telegram'}
              </Button>
            </div>

            {reqData && reqData.unmapped_items > 0 && (
              <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="req-unmapped-warning">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Позиций заказов без привязанного рецепта: <b>{reqData.unmapped_items}</b> — их сырьё не учтено. Привяжите рецепты к товарам для полной точности.</span>
              </div>
            )}

            {reqData && reqData.buy_list.length > 0 && (
              <Card className="mb-4 border-red-200"><CardContent className="p-4">
                <div className="font-semibold text-red-700 mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Не хватит на период — купить:</span>
                  <span className="text-sm text-gray-700">Бюджет закупки ≈ <b data-testid="req-budget">{money(reqData.total_buy_cost)}</b></span>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="req-buy-list">
                  {reqData.buy_list.map((b) => (
                    <Badge key={b.ingredient_id} className="bg-red-100 text-red-700">{b.name}: +{fmt(b.buy_qty)} {b.unit}{b.line_cost ? ` ≈ ${money(b.line_cost)}` : ''}</Badge>
                  ))}
                </div>
              </CardContent></Card>
            )}

            {reqLoading && <Card><CardContent className="p-6 text-center text-gray-400">Загрузка…</CardContent></Card>}
            {reqData && !reqLoading && reqData.dates.length === 0 && (
              <Card><CardContent className="p-6 text-center text-gray-400">На выбранный период нет открытых заказов с потребностью</CardContent></Card>
            )}
            {reqData && !reqLoading && reqData.dates.map((d) => (
              <Card key={d.date} className="mb-3"><CardContent className="p-0 overflow-x-auto">
                <div className="px-4 py-2.5 bg-gray-50 border-b font-semibold text-sm flex items-center justify-between">
                  <span>📅 {new Date(d.date).toLocaleDateString('ru-RU', { weekday: 'short', day: '2-digit', month: 'long' })}</span>
                  <span className="text-xs text-gray-500">{d.items.length} ингр.</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-600"><tr><th className="text-left p-3">Ингредиент</th><th className="text-right p-3">Нужно</th><th className="text-right p-3">Остаток до</th><th className="text-right p-3">Итог</th><th className="text-right p-3">Купить</th></tr></thead>
                  <tbody>
                    {d.items.map((i) => (
                      <tr key={i.ingredient_id} className={`border-t ${i.shortage > 0 ? 'bg-red-50' : ''}`} data-testid={`req-row-${d.date}-${i.ingredient_id}`}>
                        <td className="p-3">{i.name}</td>
                        <td className="p-3 text-right">{fmt(i.need)} {i.unit}</td>
                        <td className="p-3 text-right text-gray-500">{fmt(i.balance_before)} {i.unit}</td>
                        <td className={`p-3 text-right font-semibold ${i.after < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(i.after)} {i.unit}</td>
                        <td className="p-3 text-right">{i.shortage > 0 ? <Badge className="bg-red-100 text-red-700">+{fmt(i.shortage)} {i.unit}</Badge> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>
            ))}
          </TabsContent>



          {/* OPERATORS */}
          <TabsContent value="operators">
            <OperatorsPanel operators={operators} reload={load} setLinkDialog={setLinkDialog} />
          </TabsContent>

          {/* LABELS */}
          <TabsContent value="labels">
            <LabelsPage />
          </TabsContent>

          {/* RECIPES / TECH CARDS */}
          <TabsContent value="recipes">            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Input placeholder="Поиск техкарты…" value={recipeSearch} onChange={(e) => setRecipeSearch(e.target.value)} className="max-w-xs" data-testid="recipe-search" />
              <span className="text-sm text-gray-500 ml-auto">Привязано: <b className="text-emerald-700">{recipes.filter((r) => r.linked).length}</b> / {recipes.length}</span>
              <Button size="sm" variant="outline" className="bg-violet-50 border-violet-200 hover:bg-violet-100" onClick={() => setAutoOpen(true)} data-testid="auto-link-btn"><Link2 className="w-4 h-4 mr-1" />Авто-привязка по названиям</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setEditRecipe('new')} data-testid="create-recipe-btn"><Plus className="w-4 h-4 mr-1" />Создать техкарту</Button>
              <Button size="sm" variant="outline" onClick={loadRecipes} data-testid="recipes-refresh"><RefreshCw className="w-4 h-4" /></Button>
            </div>
            <div className="mb-3 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              Привяжите техкарту к товару каталога — тогда при продаже спишется сырьё и заработает расчёт потребности/маржи.
            </div>

            {/* ===== Coverage widget: which products still don't have a techkart ===== */}
            {orphans && (
              <div className="mb-4 border border-amber-200 bg-amber-50 rounded-lg" data-testid="orphans-widget">
                <button
                  type="button"
                  onClick={() => setShowOrphans(!showOrphans)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  data-testid="orphans-toggle"
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <div className="font-semibold text-amber-900">
                        Товаров без техкарты: <span data-testid="orphans-count">{orphans.totals.orphans_total}</span>
                        <span className="text-amber-700 font-normal"> из {orphans.totals.products_total}</span>
                        <span className="text-xs text-amber-600 ml-2">({orphans.totals.orphans_online} онлайн)</span>
                      </div>
                      <div className="text-xs text-amber-700 mt-0.5">
                        Техкарт: {orphans.totals.recipes_linked} из {orphans.totals.recipes_total} привязано ·
                        Покрытие каталога: <b>{Math.round(100 * (orphans.totals.products_total - orphans.totals.orphans_total) / (orphans.totals.products_total || 1))}%</b>
                      </div>
                    </div>
                  </div>
                  {showOrphans ? <ChevronDown className="w-4 h-4 text-amber-700" /> : <ChevronRight className="w-4 h-4 text-amber-700" />}
                </button>
                {showOrphans && (
                  <div className="border-t border-amber-200 px-4 py-3" data-testid="orphans-panel">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <div className="inline-flex rounded-md border border-amber-200 bg-white overflow-hidden text-xs">
                        <button className={`px-2 py-1 ${orphanFilter === 'online' ? 'bg-amber-100 font-semibold' : ''}`}
                          onClick={() => setOrphanFilter('online')} data-testid="orphan-filter-online">
                          Онлайн ({orphans.totals.orphans_online})
                        </button>
                        <button className={`px-2 py-1 border-l border-amber-200 ${orphanFilter === 'all' ? 'bg-amber-100 font-semibold' : ''}`}
                          onClick={() => setOrphanFilter('all')} data-testid="orphan-filter-all">
                          Все ({orphans.totals.orphans_total})
                        </button>
                      </div>
                      <Input
                        placeholder="Поиск по названию…"
                        value={orphanQuery}
                        onChange={(e) => setOrphanQuery(e.target.value)}
                        className="max-w-xs h-8 bg-white"
                        data-testid="orphan-search"
                      />
                    </div>
                    {(() => {
                      const filtered = orphans.orphans
                        .filter((o) => orphanFilter === 'all' || o.online_available)
                        .filter((o) => !orphanQuery || o.name.toLowerCase().includes(orphanQuery.toLowerCase()));
                      if (filtered.length === 0) {
                        return <div className="text-sm text-amber-700 py-3 text-center">Ничего не найдено</div>;
                      }
                      // Group by category
                      const groups = {};
                      filtered.forEach((o) => {
                        const key = o.category || '— без категории —';
                        (groups[key] = groups[key] || []).push(o);
                      });
                      return (
                        <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
                          {Object.entries(groups).map(([cat, items]) => (
                            <div key={cat}>
                              <div className="text-[11px] uppercase tracking-wide text-amber-800 font-semibold mb-1">
                                {cat} <span className="text-amber-600">({items.length})</span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {items.map((o) => (
                                  <div key={o.id} className="flex items-center justify-between gap-2 bg-white border border-amber-100 rounded-md px-2 py-1.5 text-sm" data-testid={`orphan-${o.id}`}>
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      {o.online_available && <span title="Онлайн" className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
                                      <span className="truncate">{o.name}</span>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-emerald-700 hover:bg-emerald-50 h-7 px-2 shrink-0"
                                      onClick={() => setEditRecipe({ __prefillProductId: o.id, __prefillName: o.name, __new: true })}
                                      data-testid={`orphan-create-${o.id}`}
                                    >
                                      <Plus className="w-3.5 h-3.5 mr-0.5" />Создать
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              {recipes.filter((f) => !recipeSearch || f.product_name.toLowerCase().includes(recipeSearch.toLowerCase())).map((f) => {
                const linkedProduct = products.find((p) => p.id === f.product_id);
                const isOpen = expandedRecipe === f.recipe_id;
                return (
                  <Card key={f.recipe_id} data-testid={`recipe-card-${f.recipe_id}`}>
                    <CardContent className="py-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <button
                          className="flex items-center gap-2 text-left flex-1 min-w-0"
                          onClick={() => setExpandedRecipe(isOpen ? null : f.recipe_id)}
                          data-testid={`recipe-toggle-${f.recipe_id}`}
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate">{f.product_name}</div>
                            <div className="text-xs text-gray-500">{f.lines.length} видов сырья · себест. {f.has_full_pricing ? money(f.total_cost) : 'нет цен'}</div>
                          </div>
                        </button>
                        <div className="flex items-center gap-2">
                          {f.linked ? <Badge className="bg-emerald-100 text-emerald-800 max-w-[260px] truncate">→ {linkedProduct?.name || 'товар'}</Badge> : <Badge className="bg-amber-100 text-amber-800">не привязан</Badge>}
                          <Button size="sm" variant="outline" onClick={() => setEditRecipe(f)} data-testid={`edit-btn-${f.recipe_id}`}><Pencil className="w-4 h-4 mr-1" />Изменить</Button>
                          <Button size="sm" variant="outline" onClick={() => setRecipeLink(f)} data-testid={`link-btn-${f.recipe_id}`}><Link2 className="w-4 h-4 mr-1" />Привязать</Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" onClick={() => deleteRecipe(f)} data-testid={`delete-btn-${f.recipe_id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="mt-3 border-t pt-3" data-testid={`recipe-lines-${f.recipe_id}`}>
                          {f.lines.length === 0 ? (
                            <div className="text-sm text-gray-400">В этой техкарте нет сырья. Добавьте состав через Excel-импорт техкарт.</div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead className="text-gray-500">
                                <tr>
                                  <th className="text-left font-medium pb-1">Ингредиент</th>
                                  <th className="text-right font-medium pb-1">Кол-во (на 1 ед.)</th>
                                  <th className="text-right font-medium pb-1">Цена/ед.</th>
                                  <th className="text-right font-medium pb-1">Стоимость</th>
                                </tr>
                              </thead>
                              <tbody>
                                {f.lines.map((ln, i) => (
                                  <tr key={i} className="border-t border-gray-100">
                                    <td className="py-1.5">{ln.name}{ln.cost_per_unit === 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 inline ml-1" title="Нет цены на ингредиент" />}</td>
                                    <td className="text-right py-1.5">{ln.quantity} {ln.unit}</td>
                                    <td className="text-right py-1.5 text-gray-500">{ln.cost_per_unit ? money(ln.cost_per_unit) : '—'}</td>
                                    <td className="text-right py-1.5">{money(ln.line_cost)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="border-t font-semibold">
                                  <td className="py-1.5" colSpan={3}>Итого себестоимость (1 ед.)</td>
                                  <td className="text-right py-1.5 text-emerald-700">{f.has_full_pricing ? money(f.total_cost) : 'нет цен'}</td>
                                </tr>
                              </tfoot>
                            </table>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {recipes.length === 0 && <div className="text-center text-gray-400 py-6">Нет техкарт</div>}
            </div>
          </TabsContent>

        </Tabs>
      </div>

      {receiptFor && <ReceiptDialog ingredient={receiptFor} onClose={() => setReceiptFor(null)} onDone={() => { setReceiptFor(null); load(); }} />}
      {editFor && <EditDialog ingredient={editFor} onClose={() => setEditFor(null)} onDone={() => { setEditFor(null); load(); }} />}
      {addOpen && <AddIngredientDialog onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />}
      {recipeLink && <RecipeLinkDialog recipe={recipeLink} products={products} onClose={() => setRecipeLink(null)} onPick={(pid) => linkRecipe(recipeLink.recipe_id, pid)} />}
      {editRecipe && <RecipeEditDialog recipe={editRecipe} ingredients={ingredients} recipes={recipes} onClose={() => setEditRecipe(null)} onDone={() => { setEditRecipe(null); loadRecipes(); }} />}
      {autoOpen && <AutoLinkDialog onClose={() => setAutoOpen(false)} onDone={() => { setAutoOpen(false); loadRecipes(); }} />}

      <Dialog open={!!linkDialog} onOpenChange={() => setLinkDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ссылка-привязка для «{linkDialog?.name}»</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Отправьте оператору. Он откроет её один раз в Telegram — и получит доступ к приложению.</p>
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
            <code className="text-xs break-all flex-1" data-testid="op-bind-link">{linkDialog?.bind_link}</code>
            <Button size="sm" variant="outline" onClick={() => copy(linkDialog.bind_link)}><Copy className="w-4 h-4" /></Button>
          </div>
          <DialogFooter><Button onClick={() => setLinkDialog(null)}>Готово</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OperatorsPanel({ operators, reload, setLinkDialog }) {
  const [name, setName] = useState('');
  const [hook, setHook] = useState(null);
  const create = async () => {
    if (!name.trim()) { toast.error('Введите имя'); return; }
    try { await axios.post(`${API}/admin/production-operators`, { name: name.trim() }, { headers: getAuthHeaders() }); toast.success('Оператор создан'); setName(''); reload(); }
    catch (e) { toast.error('Ошибка'); }
  };
  const genLink = async (o) => {
    try { const r = await axios.post(`${API}/admin/production-operators/${o.id}/bind-link`, {}, { headers: getAuthHeaders() }); setLinkDialog({ name: o.name, ...r.data }); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
  };
  const del = async (o) => { if (!window.confirm(`Удалить «${o.name}»?`)) return; await axios.delete(`${API}/admin/production-operators/${o.id}`, { headers: getAuthHeaders() }); reload(); };
  const unbind = async (o) => { await axios.post(`${API}/admin/production-operators/${o.id}/unbind`, {}, { headers: getAuthHeaders() }); reload(); };
  const setHookUrl = async () => {
    const url = `${window.location.origin}/api/production-app/telegram-webhook`;
    try { await axios.post(`${API}/admin/production-operators/webhook/set`, { url }, { headers: getAuthHeaders() }); toast.success('Webhook установлен'); checkHook(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ошибка webhook'); }
  };
  const checkHook = async () => { try { const r = await axios.get(`${API}/admin/production-operators/webhook/status`, { headers: getAuthHeaders() }); setHook(r.data?.result || r.data); } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); } };
  return (
    <div className="space-y-4">
      <Card><CardContent className="py-4 flex flex-wrap items-center gap-2">
        <Send className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-600">Webhook бота производства:</span>
        <Button size="sm" variant="outline" onClick={checkHook} data-testid="hook-status">Статус</Button>
        <Button size="sm" onClick={setHookUrl} data-testid="hook-set">Установить</Button>
        {hook && <span className="text-xs text-gray-500">URL: {hook.url || '—'}</span>}
      </CardContent></Card>
      <Card><CardContent className="py-4 flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Имя оператора (напр. Айгуль — цех)" data-testid="op-name" />
        <Button onClick={create} data-testid="op-create">Добавить</Button>
      </CardContent></Card>
      <div className="space-y-2">
        {operators.map((o) => (
          <Card key={o.id} data-testid={`op-${o.id}`}><CardContent className="py-3 flex items-center justify-between">
            <div>
              <div className="font-semibold flex items-center gap-2">{o.name}
                {o.is_bound ? <Badge className="bg-emerald-100 text-emerald-800">Привязан</Badge> : <Badge variant="outline" className="text-gray-500">Не привязан</Badge>}
              </div>
              {o.is_bound && <div className="text-xs text-gray-500">TG: {o.telegram_username ? '@' + o.telegram_username : o.telegram_user_id}</div>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => genLink(o)} data-testid={`op-link-${o.id}`}><Link2 className="w-4 h-4 mr-1" />Ссылка</Button>
              {o.is_bound && <Button size="sm" variant="outline" onClick={() => unbind(o)}>Отвязать</Button>}
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(o)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </CardContent></Card>
        ))}
        {operators.length === 0 && <div className="text-center text-gray-400 py-6">Пока нет операторов</div>}
      </div>
    </div>
  );
}

function ReceiptDialog({ ingredient, onClose, onDone }) {
  const [qty, setQty] = useState(''); const [price, setPrice] = useState(''); const [supplier, setSupplier] = useState('');
  const submit = async () => {
    if (!(qty > 0)) { toast.error('Введите количество'); return; }
    try {
      const r = await axios.post(`${API}/inventory/ingredients/${ingredient.id}/receipt`, { quantity: Number(qty), unit_price: Number(price || 0), supplier }, { headers: getAuthHeaders() });
      toast.success(`Принято. Ср. цена: ${money(r.data.new_avg_cost)}`); onDone();
    } catch (e) { toast.error('Ошибка'); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent>
      <DialogHeader><DialogTitle>Поступление: {ingredient.name}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><label className="text-sm">Количество ({ingredient.unit})</label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="dlg-receipt-qty" /></div>
        <div><label className="text-sm">Цена за единицу партии, ₸</label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="dlg-receipt-price" /></div>
        <div><label className="text-sm">Поставщик</label><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} /></div>
        <p className="text-xs text-gray-500">Средняя цена пересчитается автоматически (WAC).</p>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Отмена</Button><Button onClick={submit} data-testid="dlg-receipt-submit">Принять</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function EditDialog({ ingredient, onClose, onDone }) {
  const [qty, setQty] = useState(ingredient.current_quantity ?? 0);
  const [price, setPrice] = useState(ingredient.cost_per_unit ?? 0);
  const [min, setMin] = useState(ingredient.min_threshold || 0);
  const [max, setMax] = useState(ingredient.max_threshold || 0);
  const submit = async () => {
    try {
      await axios.put(`${API}/inventory/ingredients/${ingredient.id}/details`, {
        current_quantity: Number(qty), cost_per_unit: Number(price), min_threshold: Number(min), max_threshold: Number(max),
      }, { headers: getAuthHeaders() });
      toast.success('Сохранено'); onDone();
    } catch (e) { toast.error('Ошибка'); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent>
      <DialogHeader><DialogTitle>Редактировать: {ingredient.name}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm">Остаток ({ingredient.unit})</label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="dlg-qty" /></div>
          <div><label className="text-sm">Ср. цена за ед., ₸</label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="dlg-price" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm">Min (сигнал к закупке)</label><Input type="number" value={min} onChange={(e) => setMin(e.target.value)} data-testid="dlg-min" /></div>
          <div><label className="text-sm">Max (до сколько дозаказывать)</label><Input type="number" value={max} onChange={(e) => setMax(e.target.value)} data-testid="dlg-max" /></div>
        </div>
        <p className="text-xs text-gray-500">Для прихода партии лучше использовать кнопку «Поступление» (пересчёт WAC). Здесь — ручная корректировка.</p>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Отмена</Button><Button onClick={submit} data-testid="dlg-edit-submit">Сохранить</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function AddIngredientDialog({ onClose, onDone }) {
  const [f, setF] = useState({ name: '', unit: 'kg', current_quantity: 0, min_threshold: 0, max_threshold: 0, cost_per_unit: 0, kind: 'raw' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.name.trim()) { toast.error('Введите название'); return; }
    try { await axios.post(`${API}/inventory/ingredients`, f, { headers: getAuthHeaders() }); toast.success('Ингредиент добавлен'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent>
      <DialogHeader><DialogTitle>Новый ингредиент</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><label className="text-sm">Название</label><Input value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="new-ing-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm">Ед. изм.</label>
            <select className="w-full p-2 border rounded border-gray-200 text-sm" value={f.unit} onChange={(e) => set('unit', e.target.value)} data-testid="new-ing-unit">
              <option value="kg">кг</option><option value="pcs">шт</option><option value="L">л</option>
            </select>
          </div>
          <div><label className="text-sm">Тип</label>
            <select className="w-full p-2 border rounded border-gray-200 text-sm" value={f.kind} onChange={(e) => set('kind', e.target.value)}>
              <option value="raw">сырьё</option><option value="consumable">упаковка</option><option value="component">покупной п/ф</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm">Остаток</label><Input type="number" value={f.current_quantity} onChange={(e) => set('current_quantity', parseFloat(e.target.value) || 0)} /></div>
          <div><label className="text-sm">Цена ₸</label><Input type="number" value={f.cost_per_unit} onChange={(e) => set('cost_per_unit', parseFloat(e.target.value) || 0)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm">Min</label><Input type="number" value={f.min_threshold} onChange={(e) => set('min_threshold', parseFloat(e.target.value) || 0)} /></div>
          <div><label className="text-sm">Max</label><Input type="number" value={f.max_threshold} onChange={(e) => set('max_threshold', parseFloat(e.target.value) || 0)} /></div>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Отмена</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submit} data-testid="save-new-ing">Добавить</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function RecipeLinkDialog({ recipe, products, onClose, onPick }) {
  const [q, setQ] = useState('');
  const list = products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 100);
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-h-[80vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Привязка: {recipe.product_name}</DialogTitle></DialogHeader>
      <Input placeholder="Поиск товара…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" data-testid="product-picker-search" />
      {recipe.product_id && <Button variant="outline" className="w-full mb-2 text-red-600" onClick={() => onPick(null)} data-testid="unlink-product">Снять привязку</Button>}
      <div className="space-y-1 max-h-[50vh] overflow-y-auto">
        {list.map((p) => (
          <button key={p.id} onClick={() => onPick(p.id)} data-testid={`pick-product-${p.id}`}
            className={`w-full text-left px-3 py-2 rounded hover:bg-emerald-50 text-sm ${p.id === recipe.product_id ? 'bg-emerald-100 font-medium' : ''}`}>
            {p.name}
          </button>
        ))}
      </div>
    </DialogContent></Dialog>
  );
}

function RecipeEditDialog({ recipe, ingredients, recipes, onClose, onDone }) {
  const isNew = recipe === 'new' || (recipe && recipe.__new === true);
  const prefillProductId = (recipe && recipe.__prefillProductId) || null;
  const prefillName = (recipe && recipe.__prefillName) || '';
  const currentRecipeId = isNew ? null : (recipe.recipe_id || recipe.id || null);
  const [name, setName] = useState(isNew ? prefillName : (recipe.product_name || ''));
  const [batchYield, setBatchYield] = useState(1);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/production/recipes/${recipe.recipe_id}`, { headers: getAuthHeaders() });
        setName(data.product_name || '');
        setBatchYield(data.batch_yield || 1);
        setNotes(data.notes || '');
        setLines((data.inputs || []).map((i) => ({
          type: i.type === 'product' ? 'product' : 'ingredient',
          ingredient_id: i.ingredient_id || '',
          ingredient_name: i.ingredient_name || i.name || '',
          name: i.name || i.ingredient_name || '',
          recipe_id: i.recipe_id || '',
          product_id: i.product_id || '',
          quantity: i.quantity ?? 0,
          unit: i.unit || '',
        })));
      } catch (e) { toast.error('Ошибка загрузки техкарты'); }
      finally { setLoading(false); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const addLine = () => setLines([...lines, { type: 'ingredient', ingredient_id: '', ingredient_name: '', name: '', recipe_id: '', product_id: '', quantity: 1, unit: '' }]);
  const addSubRecipeLine = () => setLines([...lines, { type: 'product', ingredient_id: '', ingredient_name: '', name: '', recipe_id: '', product_id: '', quantity: 1, unit: 'pcs' }]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const setLine = (idx, patch) => setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const pickIngredient = (idx, ingId) => {
    const ing = ingredients.find((x) => x.id === ingId);
    setLine(idx, { ingredient_id: ingId, ingredient_name: ing ? ing.name : '', name: ing ? ing.name : '', unit: ing ? (ing.unit || '') : '' });
  };
  const pickSubRecipe = (idx, recId) => {
    const rec = (recipes || []).find((r) => r.recipe_id === recId);
    setLine(idx, { recipe_id: recId, product_id: rec ? (rec.product_id || '') : '', name: rec ? rec.product_name : '', ingredient_name: rec ? rec.product_name : '', unit: 'pcs' });
  };

  const costOf = (ingId) => {
    const ing = ingredients.find((x) => x.id === ingId);
    return ing ? Number(ing.cost_per_unit || 0) : 0;
  };
  const subRecipeCost = (subRecipeId) => {
    const r = (recipes || []).find((x) => x.recipe_id === subRecipeId);
    return r ? Number(r.total_cost || 0) : 0;
  };
  const lineCost = (l) => {
    if (l.type === 'product') return subRecipeCost(l.recipe_id) * Number(l.quantity || 0);
    return costOf(l.ingredient_id) * Number(l.quantity || 0);
  };
  const lineIsPriced = (l) => {
    if (l.type === 'product') {
      if (!l.recipe_id) return true; // no sub-recipe picked yet — not a warning
      const r = (recipes || []).find((x) => x.recipe_id === l.recipe_id);
      return !!(r && r.has_full_pricing);
    }
    if (!l.ingredient_id) return true;
    return costOf(l.ingredient_id) > 0;
  };
  const batchTotal = lines.reduce((s, l) => s + lineCost(l), 0);
  const perUnit = batchTotal / (Number(batchYield) || 1);
  const missingPrice = lines.some((l) => !lineIsPriced(l));

  const save = async () => {
    if (!name.trim()) { toast.error('Введите название техкарты'); return; }
    const clean = lines.filter((l) => {
      const q = Number(l.quantity);
      if (!(q > 0)) return false;
      if (l.type === 'product') return !!l.recipe_id;
      return !!l.ingredient_id;
    }).map((l) => {
      if (l.type === 'product') {
        return {
          type: 'product',
          recipe_id: l.recipe_id,
          product_id: l.product_id || null,
          name: l.name || l.ingredient_name || '',
          ingredient_name: l.name || l.ingredient_name || '',
          quantity: Number(l.quantity),
          unit: l.unit || 'pcs',
        };
      }
      return {
        type: 'ingredient',
        ingredient_id: l.ingredient_id,
        ingredient_name: l.ingredient_name || l.name || '',
        name: l.ingredient_name || l.name || '',
        quantity: Number(l.quantity),
        unit: l.unit || '',
      };
    });
    if (clean.length === 0) { toast.error('Добавьте хотя бы один ингредиент или полуфабрикат с количеством'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/production/recipes/custom`, {
          product_name: name.trim(),
          product_id: prefillProductId || null,
          batch_yield: Number(batchYield) || 1,
          notes,
          inputs: clean,
        }, { headers: getAuthHeaders() });
        toast.success(prefillProductId ? 'Техкарта создана и привязана к товару' : 'Техкарта создана');
      } else {
        await axios.put(`${API}/production/recipes/${recipe.recipe_id}/composition`, {
          product_name: name.trim(), batch_yield: Number(batchYield) || 1, notes, inputs: clean,
        }, { headers: getAuthHeaders() });
        toast.success('Сохранено');
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка сохранения'); }
    finally { setSaving(false); }
  };

  // Sub-recipes eligible for picking: exclude self (recipe can't reference itself)
  const subRecipeOptions = (recipes || []).filter((r) => r.recipe_id !== currentRecipeId);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[88vh] overflow-y-auto max-w-2xl" data-testid="recipe-edit-dialog">
        <DialogHeader><DialogTitle>{isNew ? 'Новая техкарта' : `Состав: ${name}`}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-gray-400">Загрузка…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-500">Название техкарты</label>
                  <span className={`text-[11px] font-semibold flex items-center gap-1 ${missingPrice ? 'text-amber-600' : 'text-emerald-700'}`} data-testid="recipe-name-per-unit">
                    {missingPrice && <AlertTriangle className="w-3 h-3" />}
                    Себест./ед: {money(perUnit)}
                  </span>
                </div>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="recipe-name-input" />
                <p className="text-[11px] text-gray-400 mt-1">Собственное название техкарты. На название товара в каталоге не влияет.</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Выход (ед. с партии)</label>
                <Input type="number" min="1" value={batchYield} onChange={(e) => setBatchYield(e.target.value)} data-testid="recipe-yield-input" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Состав (на партию)</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={addSubRecipeLine} data-testid="add-subrecipe-btn"><Plus className="w-4 h-4 mr-1" />Полуфабрикат</Button>
                  <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn"><Plus className="w-4 h-4 mr-1" />Ингредиент</Button>
                </div>
              </div>
              <div className="space-y-2">
                {lines.length === 0 && <div className="text-sm text-gray-400 py-2">Нет состава. Нажмите «Ингредиент» или «Полуфабрикат».</div>}
                {lines.map((l, idx) => {
                  const noPrice = !lineIsPriced(l);
                  const isSub = l.type === 'product';
                  return (
                  <div key={idx} className={`flex items-center gap-2 rounded-md ${noPrice ? 'bg-amber-50 px-1 py-0.5' : ''} ${isSub ? 'ring-1 ring-blue-100 bg-blue-50/40 px-1 py-0.5' : ''}`} data-testid={`line-${idx}`}>
                    {isSub ? (
                      <>
                        <span className="text-[10px] font-semibold text-blue-700 uppercase w-14 shrink-0" data-testid={`line-badge-${idx}`}>П/ф</span>
                        <select
                          value={l.recipe_id}
                          onChange={(e) => pickSubRecipe(idx, e.target.value)}
                          className="flex-1 border rounded-md px-2 py-1.5 text-sm bg-white"
                          data-testid={`line-subrecipe-${idx}`}
                        >
                          <option value="">— выберите полуфабрикат —</option>
                          {subRecipeOptions.map((r) => (
                            <option key={r.recipe_id} value={r.recipe_id}>{r.product_name}</option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <select
                        value={l.ingredient_id}
                        onChange={(e) => pickIngredient(idx, e.target.value)}
                        className="flex-1 border rounded-md px-2 py-1.5 text-sm bg-white"
                        data-testid={`line-ingredient-${idx}`}
                      >
                        <option value="">— выберите ингредиент —</option>
                        {ingredients.map((ing) => (
                          <option key={ing.id} value={ing.id}>{ing.name}{ing.unit ? ` (${ing.unit})` : ''}</option>
                        ))}
                      </select>
                    )}
                    <Input type="number" min="0" step="0.0001" value={l.quantity}
                      onChange={(e) => setLine(idx, { quantity: e.target.value })}
                      className="w-24" placeholder="кол-во" data-testid={`line-qty-${idx}`} />
                    <span className="text-xs text-gray-500 w-10">{l.unit}</span>
                    <span className={`text-xs w-20 text-right ${noPrice ? 'text-amber-600' : 'text-gray-600'}`} data-testid={`line-cost-${idx}`}>
                      {noPrice ? 'нет цены' : money(lineCost(l))}
                    </span>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeLine(idx)} data-testid={`line-remove-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-sm" data-testid="recipe-cost-summary">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Себестоимость партии ({Number(batchYield) || 1} ед.):</span>
                <span className="font-semibold text-emerald-800" data-testid="batch-total-cost">{money(batchTotal)}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-gray-600">На 1 единицу:</span>
                <span className="font-bold text-emerald-800" data-testid="per-unit-cost">{money(perUnit)}</span>
              </div>
              {missingPrice && (
                <div className="flex items-center gap-1 text-amber-600 text-xs mt-2" data-testid="missing-price-warning">
                  <AlertTriangle className="w-3.5 h-3.5" /> Есть позиции без цены — себестоимость занижена. Внесите приход на вкладке «Остатки» или добавьте цены в суб-рецепты.
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500">Примечание</label>
              <Input value={notes || ''} onChange={(e) => setNotes(e.target.value)} data-testid="recipe-notes-input" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="w-4 h-4 mr-1" />Отмена</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={saving || loading} data-testid="save-recipe-btn">
            <Save className="w-4 h-4 mr-1" />{saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AutoLinkDialog({ onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({}); // recipe_id -> bool

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/production/recipes/auto-link-suggestions`, { headers: getAuthHeaders() });
        setSuggestions(data.suggestions);
        const sel = {}; data.suggestions.forEach((s) => { sel[s.recipe_id] = s.score >= 0.6; });
        setSelected(sel);
      } catch (e) { toast.error('Ошибка загрузки предложений'); }
      finally { setLoading(false); }
    })();
  }, []);

  const toggle = (rid) => setSelected((p) => ({ ...p, [rid]: !p[rid] }));
  const chosen = suggestions.filter((s) => selected[s.recipe_id]);
  const apply = async () => {
    if (chosen.length === 0) { toast.error('Ничего не выбрано'); return; }
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/production/recipes/auto-link/apply`,
        { pairs: chosen.map((s) => ({ recipe_id: s.recipe_id, product_id: s.product_id })) }, { headers: getAuthHeaders() });
      toast.success(`Привязано: ${data.linked}`); onDone();
    } catch (e) { toast.error('Ошибка привязки'); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="auto-link-dialog">
      <DialogHeader><DialogTitle>Авто-привязка техкарт к товарам</DialogTitle></DialogHeader>
      <p className="text-sm text-gray-500">Система предложила пары по совпадению названий. Снимите галочки с неверных и подтвердите. Высокий % = надёжнее.</p>
      {loading ? <div className="py-8 text-center text-gray-400">Загрузка…</div> : (
        <>
          <div className="flex items-center justify-between text-sm py-2">
            <span>Предложений: <b>{suggestions.length}</b>, выбрано: <b className="text-emerald-700">{chosen.length}</b></span>
            <div className="flex gap-2">
              <button className="text-xs text-gray-600 underline" onClick={() => { const s = {}; suggestions.forEach((x) => { s[x.recipe_id] = true; }); setSelected(s); }}>Выбрать все</button>
              <button className="text-xs text-gray-600 underline" onClick={() => setSelected({})}>Снять все</button>
            </div>
          </div>
          <div className="divide-y border rounded-lg max-h-[55vh] overflow-y-auto">
            {suggestions.map((s) => (
              <label key={s.recipe_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50" data-testid={`auto-row-${s.recipe_id}`}>
                <input type="checkbox" checked={!!selected[s.recipe_id]} onChange={() => toggle(s.recipe_id)} className="w-4 h-4" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{s.recipe_name} <span className="text-gray-400">→</span> {s.product_name}</div>
                </div>
                <Badge className={s.score >= 0.8 ? 'bg-emerald-100 text-emerald-700' : s.score >= 0.6 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}>{Math.round(s.score * 100)}%</Badge>
              </label>
            ))}
            {suggestions.length === 0 && <div className="py-6 text-center text-gray-400">Нет подходящих совпадений</div>}
          </div>
        </>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Отмена</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={applying || chosen.length === 0} onClick={apply} data-testid="auto-apply-btn">{applying ? '…' : `Привязать выбранные (${chosen.length})`}</Button>
      </DialogFooter>
    </DialogContent></Dialog>
  );
}

export default AdminProduction;
